// redact.ts — studio.client (renderer) implementation of the W1 redaction
// contract. Mirrors studio/electron/src/main/loggy/redact.ts so renderer
// and main-process logs scrub identical secrets before reaching the wire.
//
// Per rule: think-in-systems (ai.knowledge/global) — single source of truth
// is the W1 fixture at tools/diagnostic-schema/redaction-test-vectors.json.
// This file's regex set must stay in lockstep with the electron loggy
// redact.ts and the bridge Go redact.go. Adding a redaction case = update
// the fixture + extend EVERY language helper at the same time.
//
// Per rule: stay-in-scope (ai.knowledge/global) — additive helper. The
// existing studio.client logger (`logger.ts`) is left untouched here;
// `index.ts` registers this helper as a hook so existing call sites
// keep working without per-site edits.
//
// Per rule: reference-rules-in-comments (ai.knowledge/global) — every
// regex below cites the W1 fixture id(s) it satisfies.
//
// Per rule: use-canonical-logger-not-console (ai.knowledge/platform/
// studio.client) — this file is the redaction layer for the canonical
// logger. It does NOT introduce any console.* calls.
//
// User story: when the next Shopify-style auto-update incident lands, a
// support engineer must be able to grep a single Sentry event or browser
// console transcript and reconstruct the cross-version state. That
// requires renderer-side logs to be redaction-clean (no JWTs, no OAuth
// tokens, no API keys, no email locals, no /Users/<name>/ paths, no
// Stripe IDs) AND to preserve the diagnostic surface (room IDs, agent
// IDs, version strings, hashes, ports, sizes, schema versions, boot_ids,
// device UUIDs).

// ---------------------------------------------------------------------------
// Order matters. We apply replacements most-specific-first so that, e.g., an
// email embedded in a key=value pair gets matched as email (which keeps the
// domain) rather than getting swept up by a token-shaped catch-all.
// ---------------------------------------------------------------------------

// JWT shape: <base64url>.<base64url>.<base64url> with optional Bearer or
// key=value prefix. Fixtures: jwt-typical, jwt-long,
// jwt-malformed-but-recognizable, jwt-bare-no-bearer-prefix.
const JWT_BODY = '[A-Za-z0-9_-]+\\.[A-Za-z0-9_-]+\\.[A-Za-z0-9_-]+';
const reJwtBearer = new RegExp(`Bearer (${JWT_BODY})`, 'g');
const reJwtKeyValue = new RegExp(`\\btoken=(${JWT_BODY})`, 'g');

// OAuth tokens. access_token must run before refresh — they don't overlap
// in practice because each is anchored on its key name. Fixtures:
// oauth-refresh-token-typical, oauth-refresh-token-google-shape,
// oauth-access-token-with-expiry.
const reOAuthRefresh = /\brefresh_token=([A-Za-z0-9._\-=/]+)/g;
const reOAuthAccess = /\baccess_token=([^\s]+)/g;

// Stripe IDs preserve their cus_/sub_ prefix as a diagnostic crumb.
// Fixtures: stripe-customer-id-typical, stripe-customer-id-in-billing-info,
// stripe-subscription-id-typical, stripe-subscription-id-in-context.
const reStripeCustomer = /\bcus_[A-Za-z0-9]+/g;
const reStripeSubscription = /\bsub_[A-Za-z0-9]+/g;

// API keys — non-JWT Bearer (the JWT regex runs first), x-api-key header,
// and iris-internal iris_api_key=. Fixtures: api-key-bearer-generic,
// api-key-x-api-key-header, api-key-iris-internal,
// length-preserving-* (the empty Bearer case lives here too).
const reApiKeyBearer = /\bBearer ([^\s]*)/g;
const reApiKeyXApiKey = /\bx-api-key:\s*([^\s]+)/g;
const reApiKeyIris = /\biris_api_key=([^\s]+)/g;

// Email — display-name first (RFC 5322 `Name <local@domain>`), then bare.
// Domain is preserved (tenant identification). Fixtures: email-typical,
// email-plus-tag, email-with-display-name, email-in-log-context.
const reEmailDisplay =
  /([A-Za-z][A-Za-z0-9. _-]*) <([A-Za-z0-9._%+-]+)@([A-Za-z0-9.-]+\.[A-Za-z]{2,})>/g;
const reEmailBare = /\b([A-Za-z0-9._%+-]+)@([A-Za-z0-9.-]+\.[A-Za-z]{2,})/g;

// User paths. Fixtures: path-macos-*, path-linux-*.
const reMacosUserPath = /\/Users\/[^/]+\//g;
const reLinuxHomePath = /\/home\/[^/]+\//g;

/**
 * Redact a single string per the W1 redaction contract. See
 * tools/diagnostic-schema/SCHEMA.md §7 for the contract; see
 * redaction-test-vectors.json for the exhaustive fixture set.
 *
 * Default: secret -> [REDACTED:len=N] (length-preserving).
 * Email: local -> [REDACTED] (no len; domain preserved).
 * Stripe: prefix preserved, body -> ***.
 * Paths: /Users/<name>/ and /home/<name>/ -> $HOME/.
 *
 * Non-string inputs are coerced via String() and not thrown. Strings
 * without any sensitive pattern pass through unchanged.
 */
export function redact(input: unknown): string {
  if (typeof input !== 'string') {
    return String(input);
  }

  let s = input;

  // 1. JWT — most specific (3-segment dot structure).
  s = s.replace(reJwtBearer, (_m, body) => `Bearer [REDACTED:len=${body.length}]`);
  s = s.replace(reJwtKeyValue, (_m, body) => `token=[REDACTED:len=${body.length}]`);

  // 2. OAuth tokens.
  s = s.replace(reOAuthAccess, (_m, body) => `access_token=[REDACTED:len=${body.length}]`);
  s = s.replace(reOAuthRefresh, (_m, body) => `refresh_token=[REDACTED:len=${body.length}]`);

  // 3. Stripe IDs.
  s = s.replace(reStripeCustomer, () => 'cus_***');
  s = s.replace(reStripeSubscription, () => 'sub_***');

  // 4. API keys — non-JWT Bearer (JWT bearer was already replaced above
  //    so what remains here is opaque-only).
  //
  //    Skip already-redacted bodies (e.g. `Bearer [REDACTED:len=155]`
  //    after the JWT pass) — see the matching guard in
  //    studio/electron/src/main/loggy/redact.ts for rationale.
  s = s.replace(reApiKeyBearer, (_m, body) =>
    body.length > 0 && body[0] === '[' ? `Bearer ${body}` : `Bearer [REDACTED:len=${body.length}]`
  );
  s = s.replace(reApiKeyXApiKey, (_m, body) => `x-api-key: [REDACTED:len=${body.length}]`);
  s = s.replace(reApiKeyIris, (_m, body) => `iris_api_key=[REDACTED:len=${body.length}]`);

  // 5. Email — display-name first, then bare. Domain preserved.
  s = s.replace(reEmailDisplay, (_m, _name, _local, domain) => `[REDACTED] <[REDACTED]@${domain}>`);
  s = s.replace(reEmailBare, (_m, _local, domain) => `[REDACTED]@${domain}`);

  // 6. User paths.
  s = s.replace(reMacosUserPath, '$HOME/');
  s = s.replace(reLinuxHomePath, '$HOME/');

  return s;
}

/**
 * Redact every string-typed argument in a logger call. Used by the
 * canonical logger's hook layer (`logger/index.ts`) to scrub args before
 * they reach console.log / Sentry. Non-string args pass through (Errors,
 * objects, numbers — Sentry handles their own serialization).
 *
 * The hook never throws; if redact() somehow fails on a value, the
 * original is returned. This keeps log paths fail-open — a redaction bug
 * must NEVER prevent a log line from being emitted, only scrub it.
 */
export function redactArgs(args: unknown[]): unknown[] {
  return args.map((arg) => {
    if (typeof arg === 'string') {
      try {
        return redact(arg);
      } catch {
        // Fail-open: if anything in the redaction pipeline blows up,
        // we'd rather log the unscrubbed value than swallow the line.
        return arg;
      }
    }
    return arg;
  });
}
